import { QueryClient, UseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getQueryKey, rethrowDecodedBlobError } from '@feature/utils';

import * as api from './TravelReferenceDocumentApi';
import {
  TravelReferenceDocumentContentSource,
  TravelReferenceDocumentFile,
  TravelReferenceDocumentKind,
  TravelReferenceDocumentSlot,
  TravelReferenceDocumentSlotRef,
  TravelReferenceDocumentUploadRequest,
} from './TravelReferenceDocumentTypes';

export const travelReferenceDocumentKey = getQueryKey('travelReferenceDocument');

// Non-suspense on purpose: a failed list must not blank the page it is shown on.
export function getTravelReferenceDocumentsOptions(): UseQueryOptions<TravelReferenceDocumentSlot[]> {
  return {
    queryKey: travelReferenceDocumentKey.list(),
    queryFn: () => api.getAll(),
  };
}

// Non-suspense on purpose: a failed list must not blank the settings form it shares a page with.
export function getTravelReferenceDocumentSlotsOptions(): UseQueryOptions<TravelReferenceDocumentSlot[]> {
  return {
    queryKey: travelReferenceDocumentKey.as(['slots']),
    queryFn: () => api.getSlots(),
  };
}

// A replaced file keeps its kind and role, so the upload instant is what tells its bytes from the previous ones.
export function getTravelReferenceDocumentContentOptions(
  document: TravelReferenceDocumentFile,
  source: TravelReferenceDocumentContentSource = 'resolved',
): UseQueryOptions<Blob> {
  return {
    queryKey: travelReferenceDocumentKey.as(['content', document.kind, source, document.role, document.uploadedAt]),
    queryFn: () =>
      (source === 'slot' ? api.getSlotContent(document) : api.getContent(document.kind)).catch(rethrowDecodedBlobError),
    staleTime: Infinity,
  };
}

export function useTravelReferenceDocumentsRefresh() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: travelReferenceDocumentKey.list() }),
        queryClient.invalidateQueries({ queryKey: travelReferenceDocumentKey.as(['slots']) }),
      ]),
    [queryClient],
  );
}

// The upload instant has second precision and the general guide is what roles without their own resolve to, so every content query of the kind is dropped.
function refreshKind(queryClient: QueryClient, kind: TravelReferenceDocumentKind) {
  return Promise.all([
    queryClient.resetQueries({ queryKey: travelReferenceDocumentKey.as(['content', kind]) }),
    queryClient.invalidateQueries({ queryKey: travelReferenceDocumentKey.list() }),
    queryClient.invalidateQueries({ queryKey: travelReferenceDocumentKey.as(['slots']) }),
  ]);
}

export function useTravelReferenceDocumentUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TravelReferenceDocumentUploadRequest) => api.upload(request),
    onSettled: (_data, _error, request) => refreshKind(queryClient, request.kind),
  });
}

export function useTravelReferenceDocumentDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slot: TravelReferenceDocumentSlotRef) => api.deleteSlot(slot),
    onSettled: (_data, _error, slot) => refreshKind(queryClient, slot.kind),
  });
}
